/**
 * D348, D349, D350: a theme's map kit. The map's grid, its plots' placement and fill, the
 * slab, the tier signs, the selection ring and both renderers' plumbing are shared by every
 * theme; everything that makes the map look like one theme rather than another comes from its
 * kit. A theme supplies two halves under the same id: this `MapKit` (pure, loaded with the flat
 * map) and a `ThreeKit` (`idle/three/kit.ts`, loaded with three.js). The farm's are `farmKit`
 * (`iso-kit-farm.ts`) and `farmThree` (`idle/three/farm.ts`); a farm's `theme` picks them
 * (`mapKitFor`), the farm's own by default.
 *
 * What a new theme's `MapKit` must supply:
 *
 * - `plots`: the land kept for each of its producers, by producer id (`PlotSlot`): where it
 *   starts, how big it can grow, its parcel `kind`, and what stands on it. `idleScene` fills it
 *   as the producer is bought and hands each parcel its `id`, `tier` (milestones reached),
 *   `stage` (0 to 5 as its bar fills) and `ready` (D345); `dress` may add more, such as the
 *   count, so a plot can grow tall as well as wide (D350).
 * - `chainBuildings`: where each chain's building stands, by chain id. It takes its producer's
 *   tier as `style`, so it can grow with it.
 * - `layout`: optional placement hints (D350): keep the kit's own plot positions rather than the
 *   world's seeded layout (packed tighter, say), and how many tiles a first purchase fills.
 * - `landscape`: the country beyond the fence (D329): neighbours' land, lanes, a village or
 *   whatever the theme's world has, as `CountryItem`s both renderers draw.
 * - `edgeWater`: whether the farm's river runs past the land, or none.
 * - `fieldCycle`: D353's own cycle for a plot a hand works, on the render clock (`atCycle` in
 *   `iso.ts`): how long it runs and where it grows, ripens, is harvested and is cut.
 * - `palette`, `phaseTime`, `light`, `goodWeather`, `sky`: the map's colours for the season and
 *   the hour (D288: every theme keeps seasons and day and night, looks only), the 3D light in
 *   each day phase, how good weather warms it, and the sky the country fades into.
 * - `readyBadge`: the D345 badge's colours and fallback icon, for both renderers.
 * - `top`: D350: how tall the scene stands, in world units over the ground, for the 3D camera's
 *   fit; `tall` makes the flat map's fit allow for height too, as a still's always does.
 * - `flat`: the flat map's painters, called in this order: the country and its edge water
 *   (`scenery`), the slab (shared), each parcel's ground (`ground`), its worked ground and
 *   machines (`fieldJob`, `machine`), the boundaries and the farm gate (`edges`), the standing
 *   things placed (`props`) and painted (`prop`) back to front, how tall each stands for a fit
 *   (`standing`), the season's falling weather (`weather`), and the moving smoke and ripples.
 *   `props` places the standing things for the 3D map too.
 */
import { getIdleContent, idleThemeIds } from '@hedgerow/data';
import type { Weather } from '@hedgerow/sim';
import type { CountryItem, WorldLayout } from '../idle/map.ts';
import type { IsoBuildingAt, Standing } from './IsoWorldMap.tsx';
import type { Bounds, FieldCycle, IsoParcel, MapTokens, Palette, ParcelKind } from './iso.ts';
import type { MachineAt, Prop, Puff, Ripple } from './iso-paint.ts';
import { farmKit } from './iso-kit-farm.ts';
import { computeKit } from './iso-kit-compute.ts';
import { motorsportKit } from './iso-kit-motorsport.ts';
import { arenaKit } from './iso-kit-arena.ts';

/** The land kept for one producer (D259, D267): where it starts, its size, what is on it. */
export interface PlotSlot {
  /** Where the producer's land starts and how big it can grow. */
  x: number;
  y: number;
  w: number;
  h: number;
  kind: ParcelKind;
  crop?: string;
  /** Animals stand on the land, one per owned, rather than filling tiles. */
  animal?: 'hen' | 'cow' | 'pig' | 'sheep';
  /** A building beside the land: the hen house. */
  house?: IsoBuildingAt;
  /** Bees: hives stand on the meadow, one per owned. */
  hives?: boolean;
  /** Vines: the orchard is planted as rows of vines. */
  vines?: boolean;
  /** Hops: the orchard is planted as poles with bines (D251). */
  hops?: boolean;
  /** Saffron: the meadow is low beds of crocus, growing as a field does (D251). */
  crocus?: boolean;
  /** The pasture is trodden to mud: the pigs' pen (D251). */
  mud?: boolean;
  /** Tomatoes: the orchard is a plot of glasshouses, not trees (D300). */
  glasshouse?: boolean;
  /**
   * D300, D302: tiles held back at the plot's front corner, the last filled, for its own
   * chain's building to stand on, so the two never draw on the same tile.
   */
  chainReserve?: number;
  /**
   * D344: on a kind's world, the exact tiles held back for the plot's own chain buildings, in
   * place of `chainReserve`: the crop grows on every other tile, in its usual order.
   */
  hold?: string[];
  /** D301: the piece of land beside the world this producer's plot lives on, by id. */
  land?: string;
}

/** The 3D map's light in one day phase (D288, D318). */
export interface PhaseLight {
  /** D381: how much of good weather's warmth the hour takes, 0 to 1. */
  fine: number;
  sun: string;
  sunI: number;
  /** D381: the sun's height, as a share of the world's reach. */
  sunH: number;
  sky: string;
  ground: string;
  skyI: number;
  haze: string;
  hazeT: number;
  /** D381: the top of the sky behind the world, and how far it is mixed in. */
  zenith: string;
  zenithT: number;
  rim: string;
  rimI: number;
  /** D381: how bright the tone-mapped picture is drawn. */
  exposure: number;
}

/** D345: the ready badge: its rim, its face, the icon's ink, and the icon when a row has none. */
export interface ReadyBadge {
  rim: string;
  face: string;
  ink: string;
  fallback: string;
}

/** What the flat map's `scenery` paints: the country round the land in use. */
export interface FlatScenery {
  /** The land in use, which the country is painted round. */
  far: Bounds;
  country: CountryItem[];
  /** The edge water, a line through its tiles' middles; empty for none. */
  stream: Array<[number, number]>;
  /** A lane across the country at this y, if any. */
  lane?: number | null;
  season: string;
  /** D426: the open pieces of land beside the world, kept clear of the country. */
  lands?: Bounds[];
  /** D426: the parcels, for a country that grows with the world. */
  parcels?: IsoParcel[];
  /** D430: the world's years and generations, for a country that grows with them too. */
  age?: { years: number; generations: number };
}

/** What the flat map's `edges` paints: the boundaries between plots and round the land. */
export interface FlatEdges {
  parcels: IsoParcel[];
  /** The world's own edge (D344), when there is one. */
  outline: { hedges: Array<[number, number, number, number]> } | null;
  /** Each open piece of land beside the world (D301). */
  lands: Bounds[];
  /** Off draws no boundary at all. */
  hedges: boolean;
  /** D331: the hedge and gate decorations, and the farm gate's place. D420: and the fence
   *  decoration, in a theme with no pens, for its boundary (`fenceOnBoundary`). */
  decor?: {
    fence?: string;
    hedge?: string;
    gate?: string;
    gateAt?: { x: number; y: number; px: number; py: number };
  };
}

/** The flat map's painters. Each pushes SVG markup onto `o`. */
export interface FlatKit {
  scenery(s: FlatScenery, P: Palette, o: string[]): void;
  ground(p: IsoParcel, P: Palette, o: string[]): void;
  fieldJob(p: IsoParcel, P: Palette, o: string[]): MachineAt[];
  machine(at: MachineAt, P: Palette, o: string[]): void;
  edges(e: FlatEdges, P: Palette, o: string[]): void;
  /** D422, optional: each plot's own edge, its pad and rim, painted before `edges`. */
  plotEdges?(parcels: IsoParcel[], P: Palette, o: string[]): void;
  props(p: IsoParcel, showSigns: boolean): Prop[];
  prop(s: Prop, P: Palette, o: string[]): void;
  standing(buildings: IsoBuildingAt[], parcels: IsoParcel[]): Standing[];
  weather(far: Bounds, P: Palette, o: string[]): void;
  /** D349: a theme may raise it off a plot (the compute theme's cooling towers) too. */
  smoke(buildings: IsoBuildingAt[], parcels?: IsoParcel[]): Puff[];
  ripples(parcels: IsoParcel[]): Ripple[];
}

/** D348: a theme's map kit. See the top of this file for what each part must supply. */
export interface MapKit {
  /** The theme's id, as a world's `theme` names it. */
  readonly id: string;
  readonly plots: Readonly<Record<string, PlotSlot>>;
  readonly chainBuildings: Readonly<Record<string, IsoBuildingAt>>;
  readonly layout?: {
    /** Off keeps `plots`' own positions rather than the farm's seeded layout. On by default. */
    seeded?: boolean;
    /** Tiles a field's first purchase fills (D259's `FIRST_PLOT` by default). */
    firstPlot?: number;
  };
  /**
   * D422, D423: the theme's home is a heart on the whole yard (`IsoBuildingAt.heart`), with the
   * store as part of it, in place of the home and the store. Off or left out: the two buildings.
   */
  readonly heart?: boolean;
  /** Adds the kit's own detail to a producer's parcel, after the shared fields are set. */
  dress?(p: IsoParcel, at: { id: string; owned: number; tier: number }): void;
  landscape(L: WorldLayout, plot: Bounds, lands: Bounds[]): CountryItem[];
  readonly edgeWater: boolean;
  readonly fieldCycle: FieldCycle;
  palette(season: string, time: string, tokens?: MapTokens | null, weather?: Weather): Palette;
  /** The palette's time of day for a day phase. */
  phaseTime(phase: string): string;
  light(phase: string): PhaseLight;
  readonly goodWeather: { sun: string; sky: string; mix: number; boost: number };
  sky(P: Palette, phase: string): string;
  readonly readyBadge: ReadyBadge;
  top(buildings: IsoBuildingAt[], parcels: IsoParcel[]): number;
  readonly tall: boolean;
  readonly flat: FlatKit;
}

/** Every theme's map kit, by theme id. A new theme adds its own here. */
export const MAP_KITS: Readonly<Record<string, MapKit>> = {
  farm: farmKit,
  compute: computeKit,
  motorsport: motorsportKit,
  arena: arenaKit,
};

/**
 * D420: a decoration always shows something. A theme with no pens (no plot of animals) has
 * nowhere for its fence decoration to go, so its boundary wears it instead: the compute
 * campus's fence infill, the arena stronghold's wall.
 */
export function fenceOnBoundary(kit: MapKit): boolean {
  return !Object.values(kit.plots).some((s) => s.animal);
}

/** D348: the map kit for a world's theme; the farm theme's when the theme is unknown or left out. */
export function mapKitFor(theme?: string | null): MapKit {
  return MAP_KITS[theme ?? 'farm'] ?? farmKit;
}

/** Which theme each producer id belongs to, built once from the data. */
let themeByProducer: Map<string, string> | null = null;

/**
 * D348: a world's theme. The stored theme wins: the server sends it beside the world's state
 * (`theme` on the farm's response, held by the idle store), and a caller with it passes it as
 * `stored`. Only a scene with no stored farm (a test, a made-up picture) falls back to the
 * state's own `theme`, then to its producers, whose ids each belong to one theme's content;
 * the farm's when nothing says otherwise. Without the stored theme a compute campus was drawn
 * with the farm's kit: a red barn and a farmhouse in its yard.
 */
export function themeOf(state: object, stored?: string): string {
  if (stored) return stored;
  const t = (state as { theme?: unknown }).theme;
  if (typeof t === 'string' && t) return t;
  const producers = (state as { year?: { producers?: Record<string, unknown> } }).year?.producers;
  if (!producers) return 'farm';
  if (!themeByProducer) {
    themeByProducer = new Map();
    for (const id of idleThemeIds())
      for (const p of getIdleContent(id).producers)
        if (!themeByProducer.has(p.id)) themeByProducer.set(p.id, id);
  }
  for (const id of Object.keys(producers)) {
    const theme = themeByProducer.get(id);
    if (theme) return theme;
  }
  return 'farm';
}
